import { type GameState, useGame } from './store';

/*
 * The phone's back button (the Android app, packages/mobile: MainActivity asks the page through
 * window.__amBack): it closes whatever is on top, the way Esc does on a keyboard, one thing per
 * press; on the title screen it goes back to the main list. With nothing left to close it says
 * so (false) and the app goes to the background, still running, instead of quitting the game.
 */

/** The title screen's own pages (MainMenu.tsx): back to the main list, if one is open. */
let menuBack: (() => boolean) | null = null;

export function setMenuBack(back: (() => boolean) | null): void {
  menuBack = back;
}

/** One press of back: true if it closed something. */
export function handleBack(): boolean {
  const s = useGame.getState();
  const close = (patch: Partial<GameState>) => {
    useGame.setState(patch);
    return true;
  };
  if (s.legal) return close({ legal: null });
  if (s.settingsOpen) return close({ settingsOpen: false });
  if (s.saveDialog) return close({ saveDialog: false });
  if (s.academyArticle !== null || s.helpOpen)
    return close({ helpOpen: false, academyArticle: null });
  if (s.screen !== 'game') return menuBack?.() ?? false;
  if (s.papers.length > 0) return close({ papers: s.papers.slice(1) });
  if (s.reportBook !== null) return close({ reportBook: null });
  if (s.flightCard !== null) return close({ flightCard: null });
  if (s.chatOpen) return close({ chatOpen: false });
  if (s.phoneSheet !== null) return close({ phoneSheet: null });
  if (s.opsTab !== null || s.mgmtTab !== null || s.socialTab !== null) {
    return close({ opsTab: null, mgmtTab: null, socialTab: null });
  }
  if (s.junctionAt !== null) return close({ junctionAt: null });
  if (s.photoMode) return close({ photoMode: false, photoHour: null, photoWeather: null });
  if (s.spectate) return close({ spectate: null });
  if (s.tool !== 'select') return close({ tool: 'select', placing: null, moving: null });
  if (s.selectedId !== null) return close({ selectedId: null });
  if (s.noiseMap) return close({ noiseMap: false });
  return false;
}

/** The app's hook: MainActivity calls window.__amBack() on every press of back. */
export function installBackButton(): void {
  (window as unknown as { __amBack?: () => boolean }).__amBack = handleBack;
}
